import axios from 'axios'
import { isEmpty } from 'lodash'

const api = axios.create({
  baseURL: '/tracer',
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
  },
})

const TERM_LOCAL_STORAGE_KEY = 'terminology'
// Matches the backend's own terminology cache TTL (see `ex=86400` in
// `core.terminology.add_items_to_cache`), so a browser that's had a tab open for
// a day doesn't keep serving terminology from before the authoring team's latest
// update indefinitely.
const TERM_CACHE_TTL_MS = 24 * 60 * 60 * 1000
/* we'll store terminology in local storage like:
{
  <make_model_id1>: {
     <language1>: {
      <code1>: <translation1>,
      ...
     },
     ...
  },
  "vin_map": {
    <vin1>: <make_model_id1>,
    ...
  },
  "_meta": {
    <make_model_id1>: {
      <language1>: {
        <code1>: <cachedAtEpochMs>,
        ...
      },
      ...
    },
    ...
  }
}
*/

// `codes` must always be an array by the time it's used below: a bare code
// string (e.g. passed by a caller that forgot to wrap a single code, as
// happened in `DetailInfoCircuit` before TT-2195) has no `.forEach`, which
// throws - but only once the cache already has *something* for that
// make_model_id/language, since a cold cache returns before ever touching
// `codes`. Normalizing once here, at the shared entry point, protects every
// caller instead of requiring each one to remember to pass an array.
const normalizeCodes = (codes) => {
  if (codes == null) {
    return []
  }
  return Array.isArray(codes) ? codes : [codes]
}

// A code the server didn't return a term for - never created, or deleted
// since it was last cached - gets this placeholder instead of being left out
// entirely. `description: null` reads as falsy to every existing consumer's
// `!!terms[code] && !!terms[code].description`-style check, same as the code
// being absent, but the object itself is never null, so code that does
// `data.description` without first checking `data` for truthiness (see
// `updateSubsystemDescription`) won't throw on it either.
// `_isPlaceholder` marks this as storage-only bookkeeping: it lets a code
// be cached as "confirmed no term" (see `saveTerminologyToLocalStorage`)
// without that placeholder ever being handed back to a caller, where it
// could silently overwrite a real term for the same code read from a
// different cache bucket or batch entry.
const noTermPlaceholder = (code) => ({
  code,
  description: null,
  _isPlaceholder: true,
})

const resolveTermOrPlaceholder = (code, terms) =>
  terms.hasOwnProperty(code) ? terms[code] : noTermPlaceholder(code)

const withoutPlaceholders = (terms) => {
  const real = {}
  Object.keys(terms).forEach((code) => {
    if (!terms[code]?._isPlaceholder) {
      real[code] = terms[code]
    }
  })
  return real
}

// Freshness is tracked per code, not per make_model_id/language bucket: a
// bucket-level timestamp gets reset by looking up *any* code in it (even one
// that was never cached before), which would mark every other, genuinely
// stale code in that bucket as fresh again as a side effect - see TT-2195.
const isCodeFresh = (allTerminology, make_model_id, language, code) => {
  const cachedAt =
    allTerminology?.['_meta']?.[make_model_id]?.[language]?.[code]
  return !!cachedAt && Date.now() - cachedAt < TERM_CACHE_TTL_MS
}

const loadAllTerminologyInLocalStorage = () => {
  let res = {}
  const rawTerminology = localStorage.getItem(TERM_LOCAL_STORAGE_KEY)
  if (rawTerminology) {
    res = JSON.parse(rawTerminology)
  }
  return res
}

const saveTerminologyToLocalStorage = ({
  make_model_id,
  vin,
  language,
  codes,
  terminology,
}) => {
  // `terminology` is what the two apiGet* functions format the server's
  // response into - an object keyed on the codes the server actually has a
  // term for. `codes` is every code that was actually requested over the
  // network for this call, which can be a superset of that: a code with no
  // term - never created, or deleted since it was last cached - is simply
  // absent from the server's response. Without `codes` to cross-reference
  // against, a code like that would never get an entry (or a refreshed
  // timestamp) written at all, so it would count as missing forever and be
  // re-requested on every single lookup. Caching a `{ code, description: null }`
  // placeholder for it, stamped fresh just like a real term, gives "confirmed
  // no term" its own TTL instead of retrying it on every call.
  const allTerminology = loadAllTerminologyInLocalStorage()
  let make_model_terms = allTerminology[make_model_id]
  if (!make_model_terms) {
    make_model_terms = {}
  }
  if (!make_model_terms[language]) {
    make_model_terms[language] = {}
  }

  if (!allTerminology['_meta']) {
    allTerminology['_meta'] = {}
  }
  if (!allTerminology['_meta'][make_model_id]) {
    allTerminology['_meta'][make_model_id] = {}
  }
  if (!allTerminology['_meta'][make_model_id][language]) {
    allTerminology['_meta'][make_model_id][language] = {}
  }

  const cachedAt = Date.now()
  codes.forEach((code) => {
    make_model_terms[language][code] = resolveTermOrPlaceholder(
      code,
      terminology,
    )
    allTerminology['_meta'][make_model_id][language][code] = cachedAt
  })

  if (isEmpty(allTerminology['vin_map'])) {
    allTerminology['vin_map'] = {}
  }
  if (vin) {
    allTerminology['vin_map'][vin] = make_model_id
  }
  allTerminology[make_model_id] = make_model_terms

  const allTerminologyStr = JSON.stringify(allTerminology)
  localStorage.setItem(TERM_LOCAL_STORAGE_KEY, allTerminologyStr)
}

const loadTerminologyFromLocalStorage = ({
  make_model_id,
  vin,
  language,
  codes,
}) => {
  // returns an object like:
  // { 'terminology': <results formatted like the apiGet* functions>,
  //   'missingCodes': <the codes that did not have results in localStorage> }
  codes = normalizeCodes(codes)
  const allTerminology = loadAllTerminologyInLocalStorage()
  let result = {
    terminology: {},
    missingCodes: codes,
  }
  let searchMakeModelId = make_model_id
  if (!searchMakeModelId) {
    if (!!vin) {
      const vin_map = allTerminology['vin_map']
      if (vin_map) {
        searchMakeModelId = allTerminology['vin_map'][vin]
      }
      if (!searchMakeModelId) {
        return result
      }
    } else {
      return result
    }
  }
  const makeModelTerms = allTerminology[searchMakeModelId]
  if (isEmpty(makeModelTerms) || isEmpty(makeModelTerms[language])) {
    return result
  }
  const terms = makeModelTerms[language]
  const missingCodes = []
  const terminology = {}
  codes.forEach((code) => {
    if (
      terms.hasOwnProperty(code) &&
      isCodeFresh(allTerminology, searchMakeModelId, language, code)
    ) {
      terminology[code] = terms[code]
    } else {
      missingCodes.push(code)
    }
  })
  result['terminology'] = terminology
  result['missingCodes'] = missingCodes
  return result
}

export function apiGetTerminology({ makeModelId, vin, language, codes }) {
  codes = normalizeCodes(codes)
  const cachedTerms = loadTerminologyFromLocalStorage({
    make_model_id: makeModelId,
    vin,
    language,
    codes,
  })
  if (!isEmpty(cachedTerms['missingCodes'])) {
    const missingCodes = cachedTerms['missingCodes']
    let params
    if (!!makeModelId) {
      params = new URLSearchParams({
        codes: missingCodes,
        language,
        make_model_id: makeModelId,
      })
    } else if (!!vin) {
      params = new URLSearchParams({ codes: missingCodes, language, vin })
    }
    return api
      .get(`/terminology?${params.toString()}`)
      .then((resp) => {
        const terms = resp.data['terminology']
        const result = {}
        const resultMakeModelId = resp.data['make_model_id']
        terms.forEach((term) => {
          result[term['code']] = term
        })
        saveTerminologyToLocalStorage({
          make_model_id: resultMakeModelId,
          codes: missingCodes,
          terminology: result,
          vin,
          language,
        })
        // `result` only ever holds codes the server actually has a term for -
        // a missing code with no term is a confirmed "no term", cached above
        // for next time, but never surfaced to the caller as a placeholder.
        // The codes that were already fresh in localStorage were never part of
        // this request - fold them back in so the caller still gets a value for
        // every code it originally asked for, not just the ones that were
        // actually missing.
        return { ...withoutPlaceholders(cachedTerms['terminology']), ...result }
      })
      .catch((e) => {
        // A failed request for the missing codes shouldn't cost the caller
        // the codes that were already cached and never needed the network.
        return withoutPlaceholders(cachedTerms['terminology'])
      })
  } else {
    return Promise.resolve(withoutPlaceholders(cachedTerms['terminology']))
  }
}

export function apiGetTerminologyBatch(data) {
  const dataToSearch = []
  let termsAlreadyFetched = {}
  data.forEach((termReq) => {
    // Normalize once here, same as apiGetTerminology: a caller that passes a
    // bare code string instead of an array would otherwise flow straight
    // into dataToSearch untouched, and later crash on `requestedCodes.forEach`
    // below (strings have no `.forEach`).
    const codes = normalizeCodes(termReq.codes)
    const cacheResult = loadTerminologyFromLocalStorage({
      make_model_id: termReq.make_model_id,
      vin: termReq.vin,
      language: termReq.language,
      codes,
    })
    if (isEmpty(cacheResult['missingCodes'])) {
      // Placeholders are storage-only bookkeeping - never let a "no term"
      // placeholder cached under this entry's make_model_id overwrite a real
      // term for the same code from a different entry in this batch.
      termsAlreadyFetched = {
        ...termsAlreadyFetched,
        ...withoutPlaceholders(cacheResult['terminology']),
      }
    } else {
      dataToSearch.push({ ...termReq, codes })
    }
  })
  if (!isEmpty(dataToSearch)) {
    return api
      .post('/terminology_batch', dataToSearch)
      .then((resp) => {
        const allTerms = resp.data
        let result = {}
        // The backend processes `dataToSearch` in order and returns one
        // response entry per request in the same order, so `allTerms[index]`
        // answers `dataToSearch[index]` - needed to know which codes were
        // actually requested, since the response only lists codes that have a
        // term.
        allTerms.forEach((term, index) => {
          let tmpRes = {}
          const terms = term['terminology']
          terms.forEach((trm) => {
            tmpRes[trm['code']] = trm
          })
          const requestedCodes = dataToSearch[index].codes
          saveTerminologyToLocalStorage({
            make_model_id: term['make_model_id'],
            vin: term['vin'],
            language: term['language'],
            codes: requestedCodes,
            terminology: tmpRes,
          })
          // `tmpRes` only ever holds codes the server actually has a term
          // for - a requested code absent from it is a confirmed "no term",
          // cached above for next time, but never surfaced as a placeholder
          // that could overwrite a real term for the same code from another
          // entry in this batch.
          result = { ...result, ...tmpRes }
        })
        // `termsAlreadyFetched` was read from localStorage before this request
        // went out. If the same code appears in more than one entry of this
        // batch - one already fresh-cached, another bundled with a genuine
        // miss that forced a re-fetch - the value that just came back over the
        // network is the newer one and must win, not the stale pre-fetch read.
        return { ...termsAlreadyFetched, ...result }
      })
      .catch((e) => {
        // A failed batch request shouldn't cost the caller the entries that
        // were already cached and never needed the network.
        return termsAlreadyFetched
      })
  } else {
    return Promise.resolve(termsAlreadyFetched)
  }
}
